import { Badge } from '@/common/ui/badge'
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	ResponsiveContainer,
	Cell,
} from 'recharts'
import { TTFDDistributionResponse } from '../../api/services'

interface TTFDChartProps {
	data?: TTFDDistributionResponse
	isLoading?: boolean
}

const COLORS = [
	'hsl(var(--chart-3))',
	'hsl(var(--chart-1))',
	'hsl(var(--chart-2))',
	'hsl(var(--chart-4))',
	'hsl(221 83% 70%)',
	'hsl(var(--chart-5))',
]

export default function TTFDChartSimplified({
	data,
	isLoading,
}: TTFDChartProps) {
	if (isLoading) {
		return (
			<div className="flex h-64 animate-pulse items-center justify-center rounded bg-muted">
				<div className="text-muted-foreground">กำลังโหลดข้อมูล...</div>
			</div>
		)
	}

	if (!data?.distribution || data.distribution.length === 0) {
		return (
			<div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
				<div className="text-center">
					<p className="font-medium text-muted-foreground">ไม่มีข้อมูล</p>
					<p className="mt-1 text-sm text-muted-foreground">
						ไม่พบข้อมูลเวลาในการฝากครั้งแรกในช่วงเวลาที่เลือก
					</p>
				</div>
			</div>
		)
	}

	const { distribution, summary } = data

	// Format chart data
	const chartData = distribution.map((item) => ({
		bucket: item.bucket,
		count: item.count,
		percentage: item.percentage,
	}))

	return (
		<div className="space-y-4">
			{/* Summary Badges */}
			<div className="flex flex-wrap gap-2">
				<Badge variant="secondary">
					ผู้เล่นทั้งหมด: {summary.totalPlayers.toLocaleString()}
				</Badge>
				<Badge variant="outline">
					ฝากแล้ว: {summary.playersWithDeposit.toLocaleString()} (
					{summary.conversionRate.toFixed(1)}%)
				</Badge>
				<Badge variant="outline">
					เฉลี่ย: {(summary.averageTTFD.milliseconds / 60000).toFixed(1)} นาที
				</Badge>
				<Badge variant="outline">
					มัธยฐาน: {(summary.medianTTFD.milliseconds / 60000).toFixed(1)} นาที
				</Badge>
			</div>

			<div className="h-64">
				<ResponsiveContainer width="100%" height="100%">
					<BarChart
						data={chartData}
						margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
					>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis
							dataKey="bucket"
							angle={-45}
							textAnchor="end"
							height={60}
							fontSize={12}
						/>
						<YAxis />
						<Tooltip
							formatter={(value: number, name: string) => [
								name === 'count'
									? value.toLocaleString()
									: `${value.toFixed(1)}%`,
								name === 'count' ? 'จำนวนผู้เล่น' : 'เปอร์เซ็นต์',
							]}
							labelFormatter={(label) => `ช่วงเวลา: ${label}`}
						/>
						<Bar dataKey="count" name="count" radius={[4, 4, 0, 0]}>
							{chartData.map((_, index) => (
								<Cell
									key={`cell-${index}`}
									fill={COLORS[index % COLORS.length]}
								/>
							))}
						</Bar>
					</BarChart>
				</ResponsiveContainer>
			</div>

			{/* Summary Stats */}
			<div className="mt-4 grid grid-cols-2 gap-2 text-sm md:grid-cols-5">
				<div className="text-center">
					<div className="font-semibold text-green-600 dark:text-green-400">
						{summary.playersWithDeposit.toLocaleString()}
					</div>
					<div className="text-muted-foreground">ฝากแล้ว</div>
				</div>
				<div className="text-center">
					<div className="font-semibold text-red-600 dark:text-red-400">
						{summary.playersWithoutDeposit.toLocaleString()}
					</div>
					<div className="text-muted-foreground">ยังไม่ฝาก</div>
				</div>
				<div className="text-center">
					<div className="font-semibold text-blue-600 dark:text-blue-400">
						{summary.conversionRate.toFixed(1)}%
					</div>
					<div className="text-muted-foreground">อัตราการฝาก</div>
				</div>
				<div className="text-center">
					<div className="font-semibold text-purple-600 dark:text-purple-400">
						{(summary.averageTTFD.milliseconds / 60000).toFixed(1)}
					</div>
					<div className="text-muted-foreground">เฉลี่ย (นาที)</div>
				</div>
				<div className="text-center">
					<div className="font-semibold text-orange-600 dark:text-orange-400">
						{(summary.medianTTFD.milliseconds / 60000).toFixed(1)}
					</div>
					<div className="text-muted-foreground">มัธยฐาน (นาที)</div>
				</div>
			</div>
		</div>
	)
}
